import React, { useEffect, useRef, useState } from 'react';
import {
  Wrapper,
  SelectBox,
  LeftContent,
  RightContent,
  IconButton,
  Dropdown,
  SearchWrapper,
  SearchInput,
  Option,
  OptionLabel,
  StyledPlaceholder,
  StyledSelected,
  LeftIcon,
  RightIcon,
  Checkbox,
  CheckboxIcon,
  ExecPhoto,
  ExecPhotoWrapper,
} from './styled';
import { ReactComponent as DownIcon } from 'assets/icons/ui-elements/small/caret-down-sm.svg';
import { ReactComponent as UpIcon } from 'assets/icons/ui-elements/small/caret-up-sm.svg';
import { ReactComponent as XIcon } from 'assets/icons/ui-elements/small/x-mark-sm.svg';
import { ReactComponent as LupaIcon } from 'assets/icons/ui-elements/small/magnifying_glass_sm.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as ExecutorNoPhotoIcon } from 'assets/icons/executor-no-photo-icon.svg';

export interface FilterOption {
  label: string;
  value: string;
  foto?: string;
}

export interface CategoryOption {
  label: string;
  items: FilterOption[];
}

export type MultiSelectOptions = FilterOption[] | CategoryOption[];

export interface MultiSelectDropdownProps {
  options: MultiSelectOptions;
  onChange: (values: string[]) => void;
  value?: string[];
  size?: 'sm' | 'md' | 'lg';
  iconLeft?: React.ReactNode | React.ReactNode[];
  iconRight?: React.ReactNode;
  searchable?: boolean;
  clearable?: boolean;
  placeholder?: string;
  width?: string;
  isLeftIconBlack?: boolean;
  temSelecionarTodos?: boolean;
  temCategorias?: boolean;
  categoriasComCheckbox?: boolean;
  top?: string;
  hasFoto?: boolean;
  fontSize?: string;
  searchMode?: 'local' | 'remote';
  dataMode?: 'static' | 'infinite';
  onSearch?: (term: string) => void;
  onScrollEnd?: () => void;
  showScrollbar?: boolean;
  disabled?: boolean;
  hideCaret?: boolean;
}

export const MultiSelectDropdown = React.forwardRef<HTMLDivElement, MultiSelectDropdownProps>(
  (
    {
      options,
      onChange,
      value = [],
      size = 'md',
      iconLeft,
      iconRight,
      searchable = false,
      clearable = false,
      placeholder,
      width = 'auto',
      isLeftIconBlack = false,
      temSelecionarTodos = false,
      temCategorias = false,
      categoriasComCheckbox = false,
      top = 'auto',
      hasFoto = false,
      fontSize = '14px',
      searchMode = 'local',
      dataMode = 'static',
      onSearch,
      onScrollEnd,
      showScrollbar = false,
      disabled = false,
      hideCaret = false,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownContentRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange([]);
    };

    const handleToggle = (val: string) => {
      if (value.includes(val)) {
        onChange(value.filter(v => v !== val));
      } else {
        onChange([...value, val]);
      }
    };

    const handleSelectAll = () => {
      const allValues = temCategorias
        ? (options as CategoryOption[]).flatMap(cat => cat.items.map(i => i.value))
        : (options as FilterOption[]).map(opt => opt.value);
      const isAllSelected = allValues.every(v => value.includes(v));
      onChange(isAllSelected ? [] : allValues);
    };

    const handleCategoryToggle = (category: CategoryOption) => {
      const itemValues = category.items.map(i => i.value);
      const isAllSelected = itemValues.every(v => value.includes(v));
      const newValues = isAllSelected
        ? value.filter(v => !itemValues.includes(v))
        : [...value, ...itemValues.filter(v => !value.includes(v))];
      onChange(newValues);
    };

    const selectedLabels = temCategorias
      ? (options as CategoryOption[]).flatMap(cat => cat.items.filter(i => value.includes(i.value)))
      : (options as FilterOption[]).filter(opt => value.includes(opt.value));

    const renderCheckbox = (isChecked: boolean) => (
      <Checkbox isChecked={isChecked}>
        {isChecked && (
          <CheckboxIcon>
            <CheckIcon />
          </CheckboxIcon>
        )}
      </Checkbox>
    );

    const renderItem = (item: FilterOption, key: string | number) => {
      const isChecked = value.includes(item.value);
      return (
        <Option key={key} size={size} onClick={() => handleToggle(item.value)}>
          {renderCheckbox(isChecked)}
          {hasFoto &&
            (item?.foto ? (
              <ExecPhoto src={item.foto} alt={item.label} />
            ) : (
              <ExecPhotoWrapper>
                <ExecutorNoPhotoIcon />
              </ExecPhotoWrapper>
            ))}
          <OptionLabel>{item.label}</OptionLabel>
        </Option>
      );
    };

    return (
      <Wrapper width={width} ref={ref ?? dropdownRef} fontSize={fontSize}>
        <SelectBox
          size={size}
          onClick={() => !disabled && setOpen(prev => !prev)}
          isOpen={open}
          width={width}
          disabled={disabled}
        >
          <LeftContent isLeftIconBlack={isLeftIconBlack}>
            {hasFoto && selectedLabels.length === 1 ? (
              selectedLabels[0].foto ? (
                <ExecPhoto src={selectedLabels[0].foto} alt={selectedLabels[0].label} />
              ) : (
                <ExecPhotoWrapper>
                  <ExecutorNoPhotoIcon />
                </ExecPhotoWrapper>
              )
            ) : (
              iconLeft &&
              (Array.isArray(iconLeft)
                ? iconLeft.map((Icon, idx) => <React.Fragment key={idx}>{Icon}</React.Fragment>)
                : iconLeft)
            )}
            {selectedLabels.length ? (
              <StyledSelected>
                {selectedLabels.length === 1
                  ? selectedLabels[0].label
                  : `${selectedLabels.length} ${selectedLabels.length === 1 ? 'item' : 'itens'} selecionado${
                      selectedLabels.length > 1 ? 's' : ''
                    }`}
              </StyledSelected>
            ) : (
              <StyledPlaceholder>{placeholder}</StyledPlaceholder>
            )}
          </LeftContent>
          <RightContent>
            {clearable && value.length > 0 ? (
              <IconButton onClick={handleClear}>
                <XIcon />
              </IconButton>
            ) : (
              <>
                {iconRight}
                {!hideCaret && <IconButton>{open ? <UpIcon /> : <DownIcon />}</IconButton>}
              </>
            )}
          </RightContent>
        </SelectBox>
        {open && (
          <Dropdown
            isSearchable={searchable}
            top={top}
            showScrollbar={showScrollbar}
            ref={dropdownContentRef}
            onScroll={e => {
              if (dataMode !== 'infinite' || !onScrollEnd) return;

              const el = e.currentTarget;
              const bottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 10;

              if (bottom) {
                onScrollEnd();
              }
            }}
          >
            {searchable && (
              <SearchWrapper size={size}>
                <LeftIcon>
                  <LupaIcon />
                </LeftIcon>
                <SearchInput
                  placeholder="Buscar..."
                  value={query}
                  onChange={e => {
                    const newQuery = e.target.value;
                    setQuery(newQuery);
                    if (searchMode === 'remote' && onSearch) {
                      onSearch(newQuery);
                    }
                  }}
                />
                {!hideCaret && (
                  <RightIcon onClick={() => setOpen(false)}>
                    <UpIcon />
                  </RightIcon>
                )}
              </SearchWrapper>
            )}
            {temSelecionarTodos && (
              <Option size={size} onClick={handleSelectAll} isBold>
                {renderCheckbox(
                  (temCategorias
                    ? (options as CategoryOption[]).flatMap(cat => cat.items.map(i => i.value))
                    : (options as FilterOption[]).map(o => o.value)
                  ).every(v => value.includes(v)),
                )}
                <OptionLabel>Selecionar todos</OptionLabel>
              </Option>
            )}
            {temCategorias
              ? (options as CategoryOption[]).map((category, idx) => (
                  <div key={idx}>
                    <Option
                      size={size}
                      isBold
                      categoriasComCheckbox={categoriasComCheckbox}
                      onClick={() => categoriasComCheckbox && handleCategoryToggle(category)}
                    >
                      {categoriasComCheckbox && renderCheckbox(category.items.every(i => value.includes(i.value)))}
                      <OptionLabel>{category.label}</OptionLabel>
                    </Option>
                    {category.items
                      .filter(item =>
                        searchMode === 'local' ? item.label.toLowerCase().includes(query.toLowerCase()) : true,
                      )
                      .map((item, itemIdx) => renderItem(item, `${idx}-${itemIdx}`))}
                  </div>
                ))
              : (options as FilterOption[])
                  .filter(opt =>
                    searchMode === 'local' ? opt.label.toLowerCase().includes(query.toLowerCase()) : true,
                  )
                  .map((opt, idx) => renderItem(opt, opt.value || idx))}
          </Dropdown>
        )}
      </Wrapper>
    );
  },
);
